import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Components
import PackageStatus from "../PackageStatus";
import SerialNumber from "../SerialNumber";
import PackageDetails from "../PackageDetails";
import PackageImages from "./PackageImages";
import PackageItems from "./PackageItems";
import EditPackageDrawer from "../EditPackageDrawer";
import ReceivingSlip from "./ReceivingSlip";
import SelectionTag from "../SelectionTag";
import AddItemModal from "./AddItemModel";
import AccordionViaTableRowName from "../AccordionViaTableRowName";

// Icons
import edit from "../../assets/svg/edit.svg";
import slip from "../../assets/svg/slip.svg";

// Firebase
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";

// Helpers
import { sendNotification } from "../../helper/functions";
import { setActiveItem } from "../../store/commonSlice";

const PackagesTracking = ({ steps, activeStep }) => {
  const dispatch = useDispatch();
  const {
    selectedRequest: userActiveData,
    selectedPackage: packageActiveData,
    selectedItem,
  } = useSelector((state) => state.common);

  // Local state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editPackageData, setEditPackageData] = useState({});
  const [packageItems, setPackageItems] = useState([]);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [slipValue, setSlipValue] = useState("");
  const [dateForSlip, setDateForSlip] = useState();
  const [addItemModelOpen, setAddItemModelOpen] = useState(false);

  // Ref for the receiving slip element
  const slipRef = useRef(null);

  // Generate random values for barcode and slip
  const generateRandomBarcodeValue = () => {
    return Math.floor(100000000 + Math.random() * 900000000).toString();
  };

  const generateSlipNumber = () => {
    return Math.floor(100000 + Math.random() * 900000);
  };

  // Initialize values
  useEffect(() => {
    setBarcodeValue(generateRandomBarcodeValue());
    setSlipValue(generateSlipNumber());
  }, []);

  // Set slip date when package arrives
  useEffect(() => {
    if (packageActiveData?.arrivedAt?.seconds) {
      const date = new Date(packageActiveData.arrivedAt.seconds * 1000);
      const formattedDate = date
        .toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, ".");
      setDateForSlip(formattedDate);
    }
  }, [packageActiveData?.arrivedAt]);

  // Fetch package items
  useEffect(() => {
    const fetchPackageItems = async () => {
      if (!packageActiveData?.packageItemIds?.length) {
        setPackageItems([]);
        return;
      }

      try {
        const itemPromises = packageActiveData.packageItemIds.map(
          async (itemId) => {
            if (!itemId) return null;
            const itemDocRef = doc(db, "items", itemId);
            const itemDocSnap = await getDoc(itemDocRef);
            return itemDocSnap.exists() ? itemDocSnap.data() : null;
          },
        );

        const items = await Promise.all(itemPromises);
        setPackageItems(items.filter(Boolean));
      } catch (error) {
        console.error("Error fetching package items:", error);
        setPackageItems([]);
      }
    };

    fetchPackageItems();
  }, [packageActiveData?.packageItemIds]);

  // Toggle edit drawer
  const toggleDrawer = (packageData) => {
    setEditPackageData(packageData);
    setDrawerOpen(!drawerOpen);
  };

  // Handle slip download
  const handleSlipDownload = async () => {
    try {
      const { default: html2canvas } = await import("html2canvas");
      const { default: jsPDF } = await import("jspdf");

      const slipElement = slipRef.current;
      if (!slipElement) return;

      const canvas = await html2canvas(slipElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(`${packageActiveData?.suiteNumber || "package"}_slip.pdf`);
    } catch (error) {
      console.error("Error generating slip:", error);
      toast.error("Failed to generate slip");
    }
  };

  console.log(selectedItem, "selectedItem");

  if (!packageActiveData) {
    return (
      <Box sx={{ padding: "20px", textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Select a package to view details
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {!selectedItem ? (
        <Box sx={{ paddingTop: "16px", paddingRight: "20px" }}>
          {/* Header */}
          <Box sx={{ marginLeft: "20px", mb: "16px" }}>
            <Typography variant="h5">
              Tracking: {packageActiveData?.trackingNumber || "N/A"}
            </Typography>
          </Box>

          {/* Package Status */}
          <PackageStatus steps={steps} activeStep={activeStep} />

          {/* Serial Number and Package Details */}
          <SerialNumber
            SerialNumber={packageActiveData?.serialNumber || "-"}
            packageTotalAmount={
              packageActiveData?.packageTotalAmount ||
              packageActiveData?.totalAmount
            }
            shipmentData={userActiveData}
            pageName="packages"
          />

          <PackageDetails packageActiveData={packageActiveData} />

          {/* Action Buttons - Only existing functionality */}
          <Box
            sx={{
              marginTop: "40px",
              display: "flex",
              justifyContent: "center",
              borderBottom: "1px solid #E5E5E8",
              paddingBottom: "30px",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {/* Edit Package Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: "white",
                color: "#481AA3",
                border: "1px solid #E5E5E8",
                borderRadius: "4px",
                boxShadow: "none",
                whiteSpace: "nowrap",
                marginTop: "20px",
                "&:hover": {
                  color: "#481AA3",
                  backgroundColor: "white",
                },
              }}
              onClick={() => toggleDrawer(packageActiveData)}
            >
              <img src={edit} alt="edit" style={{ marginRight: "10px" }} />
              Edit Package Details
            </Button>

            {/* Receiving Slip Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: "white",
                color: "#481AA3",
                border: "1px solid #E5E5E8",
                boxShadow: "none",
                borderRadius: "4px",
                whiteSpace: "nowrap",
                marginTop: "20px",
                "&:hover": {
                  color: "#481AA3",
                  backgroundColor: "white",
                },
              }}
              disabled={!packageActiveData?.serialNumber}
              onClick={handleSlipDownload}
            >
              <img src={slip} alt="slip" style={{ marginRight: "10px" }} />
              Receiving Slip
            </Button>

            {/* Shipping Label Button */}
            <Button
              variant="contained"
              onClick={() => {
                userActiveData?.labelUrl &&
                  window.open(userActiveData?.labelUrl, "_blank");
              }}
              disabled={!userActiveData?.labelUrl}
              sx={{
                backgroundColor: "white",
                color: "#481AA3",
                border: "1px solid #E5E5E8",
                boxShadow: "none",
                borderRadius: "4px",
                whiteSpace: "nowrap",
                marginTop: "20px",
                "&:hover": {
                  color: "#481AA3",
                  backgroundColor: "white",
                },
              }}
            >
              <img src={slip} alt="slip" style={{ marginRight: "10px" }} />
              Shipping Label
            </Button>

            {/* Customer Declaration Button */}
            <Button
              variant="contained"
              onClick={() => {
                userActiveData?.customerDeclaration &&
                  window.open(userActiveData?.customerDeclaration, "_blank");
              }}
              disabled={!userActiveData?.customerDeclaration}
              sx={{
                backgroundColor: "white",
                color: "#481AA3",
                border: "1px solid #E5E5E8",
                boxShadow: "none",
                borderRadius: "4px",
                whiteSpace: "nowrap",
                marginTop: "20px",
                "&:hover": {
                  color: "#481AA3",
                  backgroundColor: "white",
                },
              }}
            >
              <img src={slip} alt="slip" style={{ marginRight: "10px" }} />
              Customer Declaration
            </Button>
          </Box>

          {/* Selection Tag */}
          {packageActiveData?.serialNumber && (
            <SelectionTag packageActiveData={packageActiveData} />
          )}

          {/* Edit Package Drawer */}
          <EditPackageDrawer
            pageName="packages"
            open={drawerOpen}
            onClose={toggleDrawer}
            collectionName="shelfPackages"
            shipmentCollectionName="packageRequests"
            shipmentData={userActiveData}
          />

          {/* Hidden Receiving Slip for PDF generation */}
          <Box sx={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
            <ReceivingSlip
              ref={slipRef}
              name={
                packageActiveData?.suiteNumber
                  ? packageActiveData?.name
                  : "No Name"
              }
              date={dateForSlip}
              suiteNumber={
                packageActiveData?.suiteNumber
                  ? packageActiveData?.suiteNumber
                  : "No Suite"
              }
              packageId={packageActiveData?.packageId}
              barcodeValue={
                packageActiveData?.serialNumber
                  ? packageActiveData?.serialNumber
                  : barcodeValue
              }
            />
          </Box>

          {/* Package Images */}
          <PackageImages
            images={packageActiveData?.packageImages}
            title="Package Images"
          />

          {/* Package Items with Add Item functionality */}
          <PackageItems
            items={packageItems}
            packageActiveData={packageActiveData}
            userActiveData={userActiveData}
            collection="shelfPackages"
            shipmentCollection="packageRequests"
            addItemButton={
              <Button
                color="primary"
                onClick={() => {
                  setAddItemModelOpen(true);
                }}
              >
                Add Item
              </Button>
            }
          />

          {/* Add Item Modal */}
          <AddItemModal
            open={addItemModelOpen}
            toggleModel={() => setAddItemModelOpen(!addItemModelOpen)}
          />
        </Box>
      ) : selectedItem ? (
        <AccordionViaTableRowName
          handleBack={() => {
            dispatch(setActiveItem(null));
          }}
          packageClickHandler={() => {
            dispatch(setActiveItem(null));
          }}
          id={selectedItem}
          packageActiveData={packageActiveData}
          userActiveData={userActiveData}
          packageItems={packageItems}
          pageName="packages"
          packageCollection="shelfPackages"
          shipmentCollection="packageRequests"
        />
      ) : null}
    </>
  );
};

export default PackagesTracking;
